import React from 'react';
import { DossierClient, DossierTransfert, User } from './utils/types';

interface StatsCardsProps {
  activeTab: 'documents' | 'transferts' | 'users';
  dossiers: DossierClient[];
  transferts: DossierTransfert[];
  filteredUsers: User[];
  currentUserRole: string;
}

const StatsCards: React.FC<StatsCardsProps> = ({ 
  activeTab, 
  dossiers, 
  transferts, 
  filteredUsers, 
  currentUserRole 
}) => {
  const canSeeRejectedStatus = (role: string) => ['conformité', 'superviseur'].includes(role);
  const canSeeArchiveStatus = (role: string) => !['agent_saisie'].includes(role);

  const stats = activeTab === 'documents' ? {
    total: dossiers.length,
    enAttente: dossiers.filter(d => d.statut === 'en_attente').length,
    traites: dossiers.filter(d => d.statut === 'apuré').length,
    archives: dossiers.filter(d => d.statut === 'archivé').length,
    rejetes: dossiers.filter(d => d.statut === 'rejeté').length,
    voyage: dossiers.filter(d => d.typeDocument === 'voyage').length,
    transfert: dossiers.filter(d => d.typeDocument === 'transfert').length
  } : activeTab === 'transferts' ? {
    total: transferts.length,
    enAttente: transferts.filter(t => t.statut === 'en_attente').length,
    traites: transferts.filter(t => t.statut === 'apuré').length,
    archives: transferts.filter(t => t.statut === 'archivé').length,
    rejetes: transferts.filter(t => t.statut === 'rejeté').length
  } : {
    total: filteredUsers.length,
    actifs: filteredUsers.filter(u => u.isActive).length,
    superviseurs: filteredUsers.filter(u => u.role === 'superviseur').length,
    agents: filteredUsers.filter(u => u.role === 'agent_saisie').length,
    auditeurs: filteredUsers.filter(u => u.role === 'auditeur').length,
    conformite: filteredUsers.filter(u => u.role === 'conformité').length,
    admins: filteredUsers.filter(u => u.role === 'admin_bank').length,
    superAdmins: filteredUsers.filter(u => u.role === 'super_admin').length
  };

  if (activeTab === 'documents') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <StatCard
          icon="documents"
          title="Total documents"
          value={stats.total || 0}
          color="blue"
        />
        <StatCard
          icon="pending"
          title="En Attente"
          value={stats.enAttente || 0}
          color="yellow"
        />
        <StatCard
          icon="completed"
          title="Apurés"
          value={stats.traites || 0}
          color="green"
        />
        <StatCard
          icon="archive"
          title="Archives"
          value={stats.archives || 0}
          color="blue"
        />
        {canSeeRejectedStatus(currentUserRole) && (
          <StatCard
            icon="rejected"
            title="Rejetés"
            value={stats.rejetes || 0}
            color="red"
          />
        )}
      </div>
    );
  }

  if (activeTab === 'transferts') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <StatCard
          icon="transfer"
          title="Total transferts"
          value={stats.total || 0}
          color="blue"
        />
        <StatCard
          icon="pending"
          title="En Attente"
          value={stats.enAttente || 0}
          color="yellow"
        />
        <StatCard
          icon="completed"
          title="Apurés"
          value={stats.traites || 0}
          color="green"
        />
        <StatCard
          icon="archive"
          title="Archives"
          value={stats.archives || 0}
          color="blue"
        />
        {canSeeRejectedStatus(currentUserRole) && (
          <StatCard
            icon="rejected"
            title="Rejetés"
            value={stats.rejetes || 0}
            color="red"
          />
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <StatCard
        icon="users"
        title="Total utilisateurs"
        value={stats.total || 0}
        color="blue"
      />
      <StatCard
        icon="active"
        title="Actifs"
        value={stats.actifs || 0}
        color="green"
      />
      {currentUserRole === 'super_admin' ? (
        <>
          <StatCard
            icon="admin"
            title="Admin Banque"
            value={stats.admins || 0}
            color="yellow"
          />
          <StatCard
            icon="super-admin"
            title="Super Admins"
            value={stats.superAdmins || 0}
            color="red"
          />
        </>
      ) : (
        <>
          <StatCard
            icon="supervisor"
            title="Superviseurs"
            value={stats.superviseurs || 0}
            color="purple"
          />
          <StatCard
            icon="auditor"
            title="Auditeurs"
            value={stats.auditeurs || 0}
            color="orange"
          />
        </>
      )}
    </div>
  );
};

interface StatCardProps {
  icon: string;
  title: string;
  value: number;
  color: 'blue' | 'yellow' | 'green' | 'red' | 'purple' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'documents':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />;
      case 'pending':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'completed':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'archive':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />;
      case 'rejected':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />;
      case 'transfer':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />;
      case 'users':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />;
      case 'active':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'admin':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />;
      case 'super-admin':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />;
      case 'supervisor':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />;
      case 'auditor':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border hover:shadow-lg hover:scale-105 transition-all duration-300">
      <div className="flex items-center">
        <div className={`p-2 sm:p-3 ${colorClasses[color].bg} rounded-full`}>
          <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${colorClasses[color].text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {getIcon(icon)}
          </svg>
        </div>
        <div className="ml-3 sm:ml-4">
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600">{title}</p>
        </div>
      </div>
    </div>
  );
};

export default StatsCards;
